import { useMemo } from "react"
import { AUDIT_CATEGORIES } from "./audit-questions"

const MAX_CATEGORY_SCORE = 25

// Short-label overrides for categories whose `label` would overflow the
// radar label gutter. Falls back to `category.label` otherwise.
const SHORT_LABEL = {
  physical: "Physical",
  business: "Business",
  relationships: "Relations",
}

// 6-axis SVG radar. Pure SVG, zero deps, SSR-safe.
// Props: scores = { faith: 0-25, family: 0-25, ... }, size (displayed px).
//
// The SVG viewBox is intentionally larger than `size` so category labels on
// the horizontal axes have room to render without clipping. The radar
// proper keeps the same visual proportion of `size`.
export function LifeWheelRadar({ scores = {}, size = 320, label = "Integrated Life Audit" }) {
  const labelMargin = 72
  const vbSize = size + labelMargin * 2
  const cx = vbSize / 2
  const cy = vbSize / 2
  const radius = size * 0.36
  const labelRadius = size * 0.48

  const axes = AUDIT_CATEGORIES.map((cat, i) => {
    const angle = (i * Math.PI * 2) / AUDIT_CATEGORIES.length - Math.PI / 2
    return { ...cat, angle, cos: Math.cos(angle), sin: Math.sin(angle) }
  })

  const polygon = useMemo(() => {
    return axes
      .map((axis) => {
        const s = Number(scores[axis.key] || 0)
        const r = Math.max(0, Math.min(1, s / MAX_CATEGORY_SCORE)) * radius
        const x = cx + axis.cos * r
        const y = cy + axis.sin * r
        return `${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(" ")
  }, [axes, cx, cy, radius, scores])

  const rings = [0.2, 0.4, 0.6, 0.8, 1]
  const totalScore = Object.values(scores).reduce(
    (a, b) => a + Number(b || 0),
    0,
  )
  const maxTotal = AUDIT_CATEGORIES.length * MAX_CATEGORY_SCORE
  const pct = Math.round((totalScore / maxTotal) * 100)

  const ariaLabel = `${label}: ${AUDIT_CATEGORIES.map(
    (c) => `${c.label} ${scores[c.key] || 0}/${MAX_CATEGORY_SCORE}`,
  ).join(", ")}`

  return (
    <div className="flex flex-col items-center">
      <svg
        width={vbSize}
        height={vbSize}
        viewBox={`0 0 ${vbSize} ${vbSize}`}
        role="img"
        aria-label={ariaLabel}
        className="block max-w-full h-auto"
      >
        {/* Concentric reference rings */}
        {rings.map((r) => (
          <circle
            key={r}
            cx={cx}
            cy={cy}
            r={radius * r}
            fill="none"
            stroke="#e4e4e7"
            strokeWidth="1"
          />
        ))}

        {/* Axis lines */}
        {axes.map((axis) => (
          <line
            key={axis.key}
            x1={cx}
            y1={cy}
            x2={cx + axis.cos * radius}
            y2={cy + axis.sin * radius}
            stroke="#e4e4e7"
            strokeWidth="1"
          />
        ))}

        {/* Score polygon */}
        <polygon
          points={polygon}
          fill="rgba(193, 39, 47, 0.28)"
          stroke="#c1272f"
          strokeWidth="2"
        />

        {/* Dots at each axis point */}
        {axes.map((axis) => {
          const s = Number(scores[axis.key] || 0)
          const r = Math.max(0, Math.min(1, s / MAX_CATEGORY_SCORE)) * radius
          return (
            <circle
              key={`dot-${axis.key}`}
              cx={cx + axis.cos * r}
              cy={cy + axis.sin * r}
              r="3"
              fill="#c1272f"
            />
          )
        })}

        {/* Category labels */}
        {axes.map((axis) => {
          const x = cx + axis.cos * labelRadius
          const y = cy + axis.sin * labelRadius
          let anchor = "middle"
          if (axis.cos > 0.3) anchor = "start"
          if (axis.cos < -0.3) anchor = "end"
          return (
            <text
              key={`label-${axis.key}`}
              x={x}
              y={y}
              textAnchor={anchor}
              dominantBaseline="middle"
              className="fill-zinc-700"
              fontSize="12"
              fontWeight="600"
            >
              {SHORT_LABEL[axis.key] || axis.label}
            </text>
          )
        })}
      </svg>

      <div className="mt-2 text-center">
        <p className="text-3xl font-display text-zinc-900">{pct}%</p>
        <p className="text-sm text-zinc-500">
          {totalScore}/{maxTotal} overall
        </p>
      </div>

      {/* Below-chart category table for a11y / small screens */}
      <dl className="sr-only">
        {AUDIT_CATEGORIES.map((cat) => (
          <div key={cat.key}>
            <dt>{cat.label}</dt>
            <dd>
              {scores[cat.key] || 0} of {MAX_CATEGORY_SCORE}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
